/**
 * Shared calendar cell interaction module — constants, pure helpers, lane
 * assignment, and the CalendarCellInteraction reactive class.
 */

import { router } from '@inertiajs/svelte';
import { get } from 'svelte/store';
import type { CalendarEvent } from './types';
import { openEventModal, openEventModalForEdit, draggedEvent, requestEventMove } from './stores/ui';
import { showDayCellPopover, hideDayCellPopover } from './stores/dayCellPopover';
import { setCompactDragImage } from './dragImage';
import { isMultiDayEvent } from './utils';

// Re-exports so view components can import everything from one place.
export { eventColor } from './eventColors';
export {
  isMultiDayEvent,
  eventEndDate,
  eventTotalDays,
  getTodayStr,
  parseCalendarDate,
  MS_PER_DAY,
  formatEventTimeShort,
} from './utils';
export { hoveredDateStr } from './stores/dayCellPopover';
export { getWeekDates } from './weekHighlight';
export { cellClickMode } from './stores/cellClick';
export { openEventModal, openEventModalForEdit } from './stores/ui';
export type { CalendarEvent } from './types';

// ── Constants ────────────────────────────────────────────────────────────────

export const MAX_VISIBLE_EVENTS = 3;
export const MONTH_NAMES = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
] as const;
export const WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'] as const;

// ── Base type ────────────────────────────────────────────────────────────────

/** Fields shared by every calendar day-cell across all view variants. */
export interface CalendarDay {
  dateStr: string;
  day: number;
  weekday: number;
  isToday: boolean;
  isWeekend: boolean;
  events: CalendarEvent[];
}

// ── Pure helpers ─────────────────────────────────────────────────────────────

/** Filter out multi-day events, returning only single-day events. */
export function getSingleDayEvents(events: CalendarEvent[]): CalendarEvent[] {
  return events.filter((e) => !isMultiDayEvent(e));
}

/** Map event type to a CSS class name for spanning bar colouring. */
export function getSpanTypeClass(type: string): string {
  switch (type) {
    case 'appointment':
      return 'span-appointment';
    case 'travel':
      return 'span-travel';
    case 'event':
      return 'span-event';
    case 'deadline':
      return 'span-deadline';
    default:
      return 'span-default';
  }
}

// ── Lane assignment ──────────────────────────────────────────────────────────

/** Item that can be assigned to a non-overlapping lane (startCol/endCol inclusive). */
export interface LaneAssignable {
  startCol: number;
  endCol: number;
  lane: number;
}

/** Assign items to non-overlapping lanes. Sorts by startCol, mutates `lane`. */
export function assignLanes<T extends LaneAssignable>(items: T[]): T[] {
  items.sort((a, b) => a.startCol - b.startCol || b.endCol - b.startCol - (a.endCol - a.startCol));
  const lanes: number[] = [];
  for (const item of items) {
    let assigned = false;
    for (let r = 0; r < lanes.length; r++) {
      if (lanes[r] <= item.startCol) {
        item.lane = r;
        lanes[r] = item.endCol + 1;
        assigned = true;
        break;
      }
    }
    if (!assigned) {
      item.lane = lanes.length;
      lanes.push(item.endCol + 1);
    }
  }
  return items;
}

/** Return the number of lanes used (0 when the array is empty). */
export function maxLaneCount(items: { lane: number }[]): number {
  if (items.length === 0) return 0;
  return Math.max(...items.map((s) => s.lane)) + 1;
}

// ── CalendarCellInteraction ──────────────────────────────────────────────────

/**
 * Reactive class encapsulating all calendar cell interaction state & handlers
 * shared by the four calendar view components.
 */
export class CalendarCellInteraction {
  dropTargetDate = $state<string | null>(null);
  draggingEventId = $state<string | null>(null);
  #getEvents: () => CalendarEvent[];

  constructor(getEvents: () => CalendarEvent[]) {
    this.#getEvents = getEvents;
  }

  /** Open the "new event" modal for a date. */
  cellClick = (dateStr: string): void => {
    hideDayCellPopover(true);
    openEventModal(dateStr);
  };

  /** Show the day-cell popover. Caller builds the label (format varies by view). */
  cellEnter = (e: MouseEvent, dateStr: string, label: string, events: CalendarEvent[]): void => {
    const el = e.currentTarget as HTMLElement;
    showDayCellPopover(dateStr, label, events, el);
  };

  /** Hide the day-cell popover. */
  cellLeave = (): void => {
    hideDayCellPopover();
  };

  /** Start dragging an event pill or spanning bar. */
  pillDragStart = (e: DragEvent, event: CalendarEvent): void => {
    hideDayCellPopover(true);
    e.stopPropagation();
    this.draggingEventId = event.id;
    draggedEvent.set(event.id);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/event', event.id);
      setCompactDragImage(e, event.title, event.type, isMultiDayEvent(event));
    }
  };

  /** Clean up after a drag ends. */
  pillDragEnd = (): void => {
    this.draggingEventId = null;
    draggedEvent.set(null);
    this.dropTargetDate = null;
  };

  /** Allow drop on a cell when an event is being dragged. */
  cellDragOver = (e: DragEvent, dateStr: string): void => {
    if (get(draggedEvent)) {
      e.preventDefault();
      this.dropTargetDate = dateStr;
    }
  };

  /** Clear drop target when leaving a cell (unless entering a child). */
  cellDragLeave = (e: DragEvent): void => {
    const related = e.relatedTarget as Node;
    const current = e.currentTarget as Node;
    if (!current.contains(related)) {
      this.dropTargetDate = null;
    }
  };

  /** Handle the drop: move the event to the target date. */
  cellDrop = (e: DragEvent, dateStr: string): void => {
    e.preventDefault();
    this.dropTargetDate = null;
    if (!get(draggedEvent)) return;
    const eventId = e.dataTransfer?.getData('application/event');
    if (eventId) {
      const eventObj = this.#getEvents().find((ev) => ev.id === eventId);
      if (eventObj) {
        requestEventMove(eventId, eventObj, 'day', { date: dateStr });
      } else {
        router.post(
          `/calendar_events/${eventId}/move`,
          { date: dateStr },
          { preserveScroll: true },
        );
      }
    }
  };

  /** Open edit modal when clicking a spanning bar. */
  spanClick = (event: CalendarEvent): void => {
    openEventModalForEdit(event);
  };
}
